/**
 * PATH: packages/email-hub/src/feature/lib/attachmentIcons.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-ATTACHMENTICONS-001
 * PURPOSE: Single source of truth mapping an email attachment to a
 *          file-TYPE lucide icon + palette (PDF -> FileType/rose,
 *          image -> FileImage/sky, etc.), so every surface that lists
 *          attachments (the per-message AttachmentChip grid AND the
 *          sticky bottom ThreadAttachmentBar) shows the SAME glyph for
 *          the same kind of file.
 * WHY:     The icon/palette logic used to live privately inside
 *          AttachmentChip, so the bottom attachment drawer fell back to
 *          a generic paperclip — you couldn't tell a PDF from an image
 *          at a glance. Promoting it here keeps the two in lockstep.
 * RELATED:
 *   - @quotech/ui classifyAttachment (pure mime/extension -> kind/palette)
 *   - components/AttachmentChip.tsx (per-message chip)
 *   - components/ThreadAttachmentBar.tsx (sticky bottom drawer)
 */

import {
  File,
  FileArchive,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileType,
  Music,
  Presentation,
  Video,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  classifyAttachment,
  type AttachmentKind,
  type AttachmentMeta,
  type AttachmentPalette,
} from '@quotech/ui'
import type { EmailAttachment } from '../hooks/useEmails'

// kind -> glyph. Host apps pick their own glyphs from the shared
// `kind` keys the classifier returns; this is the email-hub set.
export const ICON_BY_KIND: Record<AttachmentKind, LucideIcon> = {
  image: FileImage,
  pdf: FileType,
  word: FileText,
  excel: FileSpreadsheet,
  powerpoint: Presentation,
  archive: FileArchive,
  audio: Music,
  video: Video,
  code: FileCode,
  text: FileText,
  email: FileText,
  other: File,
}

// palette -> Tailwind classes (10% tint bg, full-saturation icon).
export const PALETTE_CSS: Record<
  AttachmentPalette,
  { tint: string; icon: string; label: string }
> = {
  sky: { tint: 'bg-sky-500/10', icon: 'text-sky-600 dark:text-sky-400', label: 'text-sky-700 dark:text-sky-300' },
  rose: { tint: 'bg-rose-500/10', icon: 'text-rose-600 dark:text-rose-400', label: 'text-rose-700 dark:text-rose-300' },
  blue: { tint: 'bg-blue-500/10', icon: 'text-blue-600 dark:text-blue-400', label: 'text-blue-700 dark:text-blue-300' },
  emerald: { tint: 'bg-emerald-500/10', icon: 'text-emerald-600 dark:text-emerald-400', label: 'text-emerald-700 dark:text-emerald-300' },
  amber: { tint: 'bg-amber-500/10', icon: 'text-amber-600 dark:text-amber-400', label: 'text-amber-700 dark:text-amber-300' },
  violet: { tint: 'bg-violet-500/10', icon: 'text-violet-600 dark:text-violet-400', label: 'text-violet-700 dark:text-violet-300' },
  slate: { tint: 'bg-slate-500/10', icon: 'text-slate-600 dark:text-slate-300', label: 'text-slate-700 dark:text-slate-300' },
}

export interface AttachmentChipStyle {
  icon: LucideIcon
  tintClass: string
  iconClass: string
  labelClass: string
  meta: AttachmentMeta
}

/** Full chip style (icon + palette classes) for an attachment. */
export function attachmentChipStyle(att: EmailAttachment): AttachmentChipStyle {
  const meta = classifyAttachment(att)
  const css = PALETTE_CSS[meta.palette]
  return {
    icon: ICON_BY_KIND[meta.kind] ?? File,
    tintClass: css.tint,
    iconClass: css.icon,
    labelClass: css.label,
    meta,
  }
}

/** Just the file-type glyph for an attachment (PDF -> FileType, etc.). */
export function iconForAttachment(att: EmailAttachment): LucideIcon {
  return ICON_BY_KIND[classifyAttachment(att).kind] ?? File
}
